import { useMutation, useQuery } from "convex/react";
import { CheckCircle2Icon, HelpCircleIcon, SendIcon } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { extractAlwaysAllowToolName } from "../../../../email-assistant/core/askQuestionPermissions";
import type { AskQuestionPayload } from "../../../../email-assistant/core/toolInteractions";
import { api } from "../../../convex/_generated/api";

interface AskQuestionCardProps {
	payload: AskQuestionPayload;
	/** True = latest turn, render inputs; false = already answered, render read-only. */
	isActive: boolean;
	/** Called with the formatted answer message when the operator submits. */
	onSubmit?: (answerMessage: string) => void;
	/** Disabled while a send is in flight — only affects the Send button. */
	disabled?: boolean;
}

type AnswerMap = Record<string, string | string[]>;

function isAnswered(
	q: AskQuestionPayload["questions"][0],
	v: unknown,
): boolean {
	if (q.kind === "choice" && q.multiSelect) {
		return Array.isArray(v) && v.length > 0;
	}
	return typeof v === "string" && v.trim().length > 0;
}

/** Formats answers as `id: value` lines so the agent reads them unambiguously. */
function formatAnswerMessage(
	payload: AskQuestionPayload,
	answers: AnswerMap,
): string {
	const lines = ["Answers to your questions:", ""];
	for (const q of payload.questions) {
		const a = answers[q.id];
		const value = Array.isArray(a) ? a.join(", ") : a;
		if (value && value.trim().length > 0) {
			lines.push(`- ${q.id}: ${value}`);
		} else {
			lines.push(`- ${q.id}: (not provided)`);
		}
	}
	return lines.join("\n");
}

export function AskQuestionCard({
	payload,
	isActive,
	onSubmit,
	disabled,
}: AskQuestionCardProps) {
	const [answers, setAnswers] = useState<AnswerMap>({});
	const [submitted, setSubmitted] = useState(false);
	// "Yes, and always allow X" convention persists the preference before forwarding.
	// Tool-name validation is server-side; invalid names just no-op.
	const toolMetadata = useQuery(api.users.listAutoApprovableTools);
	const toggleAutoApprove = useMutation(api.users.toggleAutoApproveTool);

	const updateText = (id: string, value: string) => {
		setAnswers((prev) => ({ ...prev, [id]: value }));
	};

	const toggleChoice = (id: string, option: string, multi: boolean) => {
		setAnswers((prev) => {
			if (!multi) return { ...prev, [id]: option };
			const current = (prev[id] as string[] | undefined) ?? [];
			const next = current.includes(option)
				? current.filter((c) => c !== option)
				: [...current, option];
			return { ...prev, [id]: next };
		});
	};

	const requiredAnswered = payload.questions.every(
		(q) => q.optional || isAnswered(q, answers[q.id]),
	);

	const handleSubmit = async () => {
		if (!requiredAnswered || submitted) return;
		// Scan chosen options for "Yes, and always allow X"; persist matches against known tool names.
		const knownToolNames = new Set(toolMetadata?.map((t) => t.name) ?? []);
		const toolsToEnable: string[] = [];
		for (const value of Object.values(answers)) {
			const optionStrings = Array.isArray(value) ? value : [value];
			for (const opt of optionStrings) {
				const toolName = extractAlwaysAllowToolName(opt);
				if (toolName && knownToolNames.has(toolName)) {
					toolsToEnable.push(toolName);
				}
			}
		}
		setSubmitted(true);
		// Persist "always allow" choices BEFORE forwarding so the next agent turn sees them.
		// Errors don't block the answer — chat must always make progress.
		await Promise.all(
			toolsToEnable.map((toolName) =>
				toggleAutoApprove({ toolName, enabled: true }).catch(() => {
					// Swallow — unknown-name rejection isn't user-actionable.
				}),
			),
		);
		const message = formatAnswerMessage(payload, answers);
		onSubmit?.(message);
	};

	const showInputs = isActive && !submitted;
	const answeredState = submitted;

	return (
		<div className="my-3 rounded-xl border bg-card text-card-foreground shadow-sm">
			<div className="flex items-start gap-3 border-b px-4 py-3">
				<div className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
					{answeredState ? (
						<CheckCircle2Icon className="size-4" />
					) : (
						<HelpCircleIcon className="size-4" />
					)}
				</div>
				<div className="flex-1">
					<div className="text-sm font-semibold">
						{answeredState
							? "Questions answered"
							: "Clerical needs a few details"}
					</div>
					{payload.intro ? (
						<p className="mt-0.5 text-xs leading-snug text-muted-foreground">
							{payload.intro}
						</p>
					) : null}
				</div>
			</div>

			<div className="space-y-4 px-4 py-4">
				{payload.questions.map((q, idx) => {
					const labelId = `aq-${q.id}-${idx}`;
					const value = answers[q.id];
					return (
						<div key={q.id} className="space-y-1.5">
							<Label htmlFor={labelId} className="text-xs font-medium">
								<span className="mr-1.5 inline-flex size-4 items-center justify-center rounded-full bg-muted text-[10px] font-semibold tabular-nums text-muted-foreground">
									{idx + 1}
								</span>
								{q.question}
								{q.optional ? (
									<span className="ml-1.5 text-xs font-normal text-muted-foreground">
										(optional)
									</span>
								) : null}
							</Label>

							{!showInputs ? (
								<div className="rounded-md border bg-muted/50 px-3 py-1.5 text-sm">
									{(Array.isArray(value) ? value.join(", ") : value) || (
										<span className="italic text-muted-foreground">
											(not provided)
										</span>
									)}
								</div>
							) : q.kind === "choice" ? (
								<div className="flex flex-wrap gap-1.5">
									{q.options?.map((opt) => {
										const selected = q.multiSelect
											? Array.isArray(value) && value.includes(opt)
											: value === opt;
										return (
											<Button
												key={opt}
												type="button"
												size="sm"
												variant={selected ? "default" : "outline"}
												disabled={submitted}
												onClick={() =>
													toggleChoice(q.id, opt, Boolean(q.multiSelect))
												}
												className="h-auto rounded-full px-3 py-1 text-xs"
											>
												{opt}
											</Button>
										);
									})}
								</div>
							) : q.kind === "long_text" ? (
								<Textarea
									id={labelId}
									value={(value as string) ?? ""}
									onChange={(e) => updateText(q.id, e.target.value)}
									placeholder={q.placeholder}
									rows={3}
									disabled={submitted}
									className="text-sm"
								/>
							) : (
								<Input
									id={labelId}
									value={(value as string) ?? ""}
									onChange={(e) => updateText(q.id, e.target.value)}
									placeholder={q.placeholder}
									disabled={submitted}
									className="text-sm"
								/>
							)}
						</div>
					);
				})}
			</div>

			{showInputs ? (
				<div className="flex items-center justify-between gap-2 border-t bg-muted/40 px-4 py-3">
					<p className="text-xs text-muted-foreground">
						{requiredAnswered
							? "Ready to send"
							: `${
									payload.questions.filter(
										(q) => !q.optional && !isAnswered(q, answers[q.id]),
									).length
								} of ${
									payload.questions.filter((q) => !q.optional).length
								} left`}
					</p>
					<Button
						size="sm"
						onClick={handleSubmit}
						disabled={!requiredAnswered || submitted || disabled}
						className="gap-1.5"
					>
						<SendIcon className="size-3.5" />
						Send answers
					</Button>
				</div>
			) : null}
		</div>
	);
}
